import { KeyboardArrowLeft, Logout, Notifications, Check, Cancel } from "@mui/icons-material";
import {
  AppBar,
  Toolbar,
  Drawer,
  Typography,
  IconButton,
  Badge,
  ThemeProvider,
  useMediaQuery,
  Divider,
  Tooltip,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Chip,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import { Fragment, useEffect, useState } from "react";
import biosenseAPI from "../../../../api/biosense";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../../components/languageSwitcher";
import cardOrListTheme from "../../../../assets/cardOrListTheme";
import zIndices from "../../../../assets/zIndices";
import TopBarTabs from "./topBarTabs";
import { CloudQueue, DeviceHub, Person, Work, ErrorOutline } from "@mui/icons-material";
import { formatAlertMessage } from "../../../../utils/formatAlertMessage";

const theme = cardOrListTheme;

const TopBar = ({ setState, state, topBarState, setTopBarState, currentStack, onTabChange }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const alerts = topBarState.alerts;
  useEffect(() => {
    biosenseAPI.getUserProfile().then((res) => setTopBarState({ firstname: res.user.firstname }));
    biosenseAPI.getPendingAlertList({ includeIgnoredAlerts: false }).then((res) => {
      setTopBarState({ alerts: res.alerts });
    }); 
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("tokenExpiresAt");
    localStorage.removeItem("ecois_tokenExpiresAt");
    localStorage.removeItem("ecois_token");
    localStorage.removeItem("token");
    navigate("/landing");
    setState({
      ecois_tokenExpiresAt: null,
      tokenExpiresAt: null,
      ecois_token: null,
      token: null,
    });
  };

  const handleViewAll = () => {
    onTabChange("alertlist", 0);
  };

  const onClickBack = () => {
    const { pageTabStack, pageIdxStack } = state;
    if (pageTabStack.length === 0 || pageIdxStack.length === 0) {
      setState({ pageIdx: state.pageIdx - 1 });
      if (state.pageIdx > 0) {
        onTabChange(state.pageTab, state.pageIdx - 1);
      }
    } else {
      const newPageTabStack = pageTabStack.slice(0, -1);
      const newPageIdxStack = pageIdxStack.slice(0, -1);
      const newTab = pageTabStack[pageTabStack.length - 1];
      const newIdx = pageIdxStack[pageTabStack.length - 1];
      setState({
        pageTab: newTab,
        pageIdx: newIdx,
        pageTabStack: newPageTabStack,
        pageIdxStack: newPageIdxStack,
      });
      onTabChange(newTab, newIdx);
    }
  };

  const renderNavigator = () => (
    <IconButton
      color='inherit'
      size='small'
      disabled={state.pageIdx === 0}
      onClick={onClickBack}
      sx={{ backgroundColor: "#777777" }}
    >
      <KeyboardArrowLeft />
    </IconButton>
  );

  const renderNavigatorText = () => (
    <Typography color='inherit' variant='body2'>
      {currentStack}
    </Typography>
  ); //TODO change display to the main page, not at top bar

  const renderContentSet = () => (
    <Box display='flex' alignItems='center'>
      <ForecastIcon /> {/* 新增forecast按钮，在alerts左侧 */}
      <NotificationIcon color='inherit' alerts={alerts} handleViewAll={handleViewAll} topBarState={topBarState} setTopBarState={setTopBarState} />
      <LanguageSwitcher />
      <Tooltip title={t("main.topbar_logout")}>
        <IconButton color='inherit' size='small' onClick={handleLogout}>
          <Logout />
        </IconButton>
      </Tooltip>
      <Typography color='inherit' variant='body2'>
        {topBarState.firstname || "Welcome User"}
      </Typography>
    </Box>
  );

  //// Phone UI
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  if (isPhone) {
    return (
      <StyledAppBar position='static'>
        <StyledToolbar>
          <Box flexGrow={1}>
            <TopBarTabs isPhone setState={setState} state={state} onTabChange={onTabChange} />
            {renderNavigator()}
          </Box>
          {renderContentSet()}
        </StyledToolbar>
      </StyledAppBar>
    );
  }
  ////

  return (
    <Fragment>
      <StyledAppBar position='static'>
        <StyledToolbar>
          <Box flexGrow={1}>
            {renderNavigator()}
            <TopBarTabs setState={setState} state={state} onTabChange={onTabChange} />
          </Box>
          {renderContentSet()}
        </StyledToolbar>
      </StyledAppBar>
    </Fragment>
  );
};

export default TopBar;

const StyledAppBar = styled(AppBar)({
  backgroundColor: "rgba(0, 0, 0, 0.7)", // Black with transparency
  position: "fixed",
  top: 0,
  zIndex: 3,
  boxSizing: "border-box",
  minWidth: "100vw",
  minHeight: "64px",
});

const StyledToolbar = styled(Toolbar)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const Container = styled("div")({
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  color: "white",
  width: "100%",
  position: "fixed",
  top: 0,
  gap: "0.5em",
  zIndex: zIndices.topbar,
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  padding: "0 10px",
  transition: "min-height 0.25s ease-out, left 0.25s ease-out",
});

const RightContainer = styled("div")({
  color: "white",
  position: "absolute",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  gap: "0.5em",
  right: "10px",
  minHeight: "48px",
  padding: "0 10px",
  zIndex: zIndices.topbar,
});

const RightContainer2 = styled("div")({
  color: "white",
  position: "absolute",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  gap: "0.5em",
  top: "48px",
  right: "10px",
  minHeight: "48px",
  padding: "0 10px",
  zIndex: zIndices.topbar,
});

const typeToIcon = {
  device: <DeviceHub />,
  user: <Person />,
  project: <Work />,
};

const ForecastIcon = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [forecastData, setForecastData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 获取天气预报数据（特别天气提示和九天天气预报）
  const fetchForecast = async () => {
    setLoading(true);
    setError(null);

    try {
      // 1. 获取特别天气提示（swt）
      const swtResponse = await fetch(
        "https://data.weather.gov.hk/weatherAPI/opendata/weather.php?dataType=swt&lang=en",
      );
      const swtData = await swtResponse.json();

      // 2. 获取九天天气预报（fnd）
      const fndResponse = await fetch(
        "https://data.weather.gov.hk/weatherAPI/opendata/weather.php?dataType=fnd&lang=en",
      );
      const fndData = await fndResponse.json();

      // 整合数据：特别天气提示（最近3条）和天气预报（前3天）
      const swtItems = swtData.swt || [];
      const fndItems = (fndData.weatherForecast || []).map((item) => ({
        desc: item.forecastWeather,
        updateTime: item.updateTime || new Date().toISOString(),
      }));

      // 合并并按时间排序（最新在前）
      const allItems = [...swtItems, ...fndItems]
        .sort((a, b) => new Date(b.updateTime) - new Date(a.updateTime))
        .slice(0, 3); // 取最近3条

      setForecastData(allItems);
    } catch (err) {
      setError("Failed Obtaining Weather Forecast. Please try later");
      console.error("Forecast API error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForecast();
  }, []);

  const handleClick = () => {
    fetchForecast(); // 点击时刷新数据
    setIsDrawerOpen(true);
  };

  const handleClose = () => {
    setIsDrawerOpen(false);
  };

  return (
    <>
      <IconButton
        aria-label='show weather forecast'
        color='inherit'
        size='small'
        onClick={handleClick}
      >
        <Badge
          badgeContent={
            ""
            //forecastData.length > 0 ? forecastData.length : undefined
          }
          color='primary'
        >
          <CloudQueue /> {/* 使用天气图标 */}
        </Badge>
      </IconButton>

      <Drawer anchor='right' open={isDrawerOpen} onClose={handleClose}>
        <AppBar position='static' color='transparent' sx={{ boxShadow: "none" }}>
          <Toolbar>
            <Typography variant='h6' sx={{ flexGrow: 1 }}>
              Weather Forecast (From HKO, Experimental)
            </Typography>
            <Button color='inherit' onClick={handleClose}>
              Close
            </Button>
          </Toolbar>
          <Divider orientation='horizontal' />
        </AppBar>

        <List>
          {loading ? (
            <ListItem sx={{ justifyContent: "center" }}>
              <CircularProgress size={24} color='primary' />
            </ListItem>
          ) : error ? (
            <ListItem sx={{ color: "error.main" }}>{error}</ListItem>
          ) : forecastData.length === 0 ? (
            <ListItem sx={{ color: "text.secondary" }}>No recent weather forecasts</ListItem>
          ) : (
            forecastData.map((item, index) => (
              <ListItem key={index} sx={{ py: 1.5 }}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant='body1' sx={{ fontWeight: "medium" }}>
                    {item.desc || "No description"}
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                    {new Date(item.updateTime).toLocaleString("zh-CN", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </Box>
                <Divider sx={{ my: 0.5 }} />
              </ListItem>
            ))
          )}
        </List>
      </Drawer>
    </>
  );
};

const NotificationIcon = ({ alerts, handleViewAll, topBarState, setTopBarState }) => {
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loadingIds, setLoadingIds] = useState([]); // 存储正在处理的alert ID
  const [errorIds, setErrorIds] = useState({}); // 存储处理失败的alert ID及错误信息

  const isDev = process.env.NODE_ENV === "development";

  const handleClick = () => {
    setIsDrawerOpen(true);
  };

  const handleClose = () => {
    setIsDrawerOpen(false);
    setErrorIds({}); // 关闭抽屉时清除错误信息
  };

  const handleResolve = async (alertId) => {
    setLoadingIds((prev) => [...prev, alertId]);
    setErrorIds((prev) => ({ ...prev, [alertId]: null }));
    
    try {
      // 向API发送解决请求
      await biosenseAPI.resolveAlert({ alert_id: alertId});
      // 更新警报列表
      await biosenseAPI.getPendingAlertList({ includeIgnoredAlerts: false }).then((res) => {
        setTopBarState({ alerts: res.alerts });
      }); 
    } catch (error) {
      console.error("Failed to resolve alert:", error);
      setErrorIds((prev) => ({ ...prev, [alertId]: error.message || "Failed to resolve alert" }));
    } finally {
      setLoadingIds((prev) => prev.filter(id => id !== alertId));
    }
  };

  const handleIgnore = async (alertId) => {
    setLoadingIds((prev) => [...prev, alertId]);
    setErrorIds((prev) => ({ ...prev, [alertId]: null }));
    
    try {
      // 向API发送忽略请求
      await biosenseAPI.ignoreAlert({ alert_id: alertId});
      // 更新警报列表
      await biosenseAPI.getPendingAlertList({ includeIgnoredAlerts: false }).then((res) => {
        setTopBarState({ alerts: res.alerts });
      }); 
    } catch (error) {
      console.error("Failed to ignore alert:", error);
      setErrorIds((prev) => ({ ...prev, [alertId]: error.message || "Failed to ignore alert" }));
    } finally {
      setLoadingIds((prev) => prev.filter(id => id !== alertId));
    }
  };

  return (
    <>
      <IconButton
        aria-label='show new notifications'
        color='inherit'
        size='small'
        onClick={handleClick}
      >
        <Badge badgeContent={topBarState.alerts.length} color='error'>
          <Notifications />
        </Badge>
      </IconButton>
      <Drawer anchor='right' open={isDrawerOpen} onClose={handleClose}>
        <AppBar position='static' color='transparent' sx={{ boxShadow: "none" }}>
          <Toolbar>
              <Button
                onClick={() => {
                  handleClose();
                  handleViewAll();
                }}
                color='inherit'
              >
                View All
              </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button color='inherit' onClick={handleClose}>
              Back
            </Button>
          </Toolbar>
          <Divider orientation='horizontal' />
        </AppBar>
        <List>
          {alerts.map((alert) => {
            const isLoading = loadingIds.includes(alert.id);
            const hasError = errorIds[alert.id] !== undefined && errorIds[alert.id] !== null;
            
            return (
              <ListItem key={alert.id}>
                <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                  <ListItemIcon>{typeToIcon[alert.type] || null}</ListItemIcon>
                  <Chip label={alert?.device_alias || ""} />
                </Box>
                <ListItemText
                  primary={
                    <Typography variant='body1'>{formatAlertMessage(t, alert.alert_text)}</Typography>
                  }
                  sx={{ flexGrow: 1 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title={t("main.resolve")}>
                    <IconButton 
                      onClick={() => handleResolve(alert.id)}
                      disabled={isLoading}
                      color={isLoading ? "disabled" : hasError ? "error" : "success"}
                    >
                      {isLoading ? (
                        <CircularProgress size={20} />
                      ) : hasError ? (
                        <ErrorOutline />
                      ) : (
                        <Check />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("main.ignore")}>
                    <IconButton 
                      onClick={() => handleIgnore(alert.id)}
                      disabled={isLoading}
                      color={isLoading ? "disabled" : hasError ? "error" : "inherit"}
                    >
                      {isLoading ? (
                        <CircularProgress size={20} />
                      ) : hasError ? (
                        <ErrorOutline />
                      ) : (
                        <Cancel />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
                {hasError && (
                  <Box sx={{ mt: 1, ml: 8, color: 'error.main', fontSize: '0.875rem' }}>
                    {errorIds[alert.id]}
                  </Box>
                )}
                <Divider />
              </ListItem>
            );
          })}
        </List>
      </Drawer>
    </>
  );
};